// Wave AW.3-React (2026-05-16) — PROMISE (RFQ) 상세 화면
// Wave UX-2.1 Item 1 sweep (2026-05-23) — 4 locale i18n 마이그레이션.
// API: GET /buyer-app/rfq/:id, POST /buyer-app/rfq/:id/quotes, DELETE quotes

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuctionBuyerQuoteModal from './AuctionBuyerQuoteModal';
import MarketPriceCard from '../MarketPriceCard';  // W6 — AUTODEX 시세 (외부 바이어, KRW+USD)
import { useT, useLocale } from '../../i18n/I18nProvider';
import { formatKrwAmount, formatKrwAmountWithConvert, useExchangeRates } from '../../i18n/currency';
import { confirm } from './ConfirmDialog';
import { showToast } from './Toast';

function formatKrw(n, t) { return formatKrwAmount(n, t); }

function getPhotoUrls(rfq) {
  var photos = rfq.photos;
  var urls = [];
  var origin = API.replace(/\/api$/, '');
  function toUrl(p) {
    if (!p) return null;
    if (/^https?:/.test(p)) return p;
    return origin + (p.charAt(0) === '/' ? p : '/' + p);
  }
  if (Array.isArray(photos)) {
    photos.forEach(function(p){ var u = toUrl(p); if (u) urls.push(u); });
  } else if (photos && typeof photos === 'object') {
    Object.keys(photos).forEach(function(k){ var u = toUrl(photos[k]); if (u) urls.push(u); });
  }
  // extras
  if (Array.isArray(rfq.extrasPhotos)) {
    rfq.extrasPhotos.forEach(function(p){ var u = toUrl(p); if (u) urls.push(u); });
  }
  return urls;
}

function AuctionBuyerPromiseDetail() {
  var navigate = useNavigate();
  var params = useParams();
  var rfqId = params.id;
  var t = useT();
  var loc = useLocale();
  var rateState = useExchangeRates();

  var [rfq, setRfq] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [quoteModalOpen, setQuoteModalOpen] = useState(false);
  var [photoIdx, setPhotoIdx] = useState(0);

  var load = useCallback(function(){
    setLoading(true); setError('');
    authBuyerFetch(API + '/buyer-app/rfq/' + rfqId, {})
      .then(function(r){
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d){
        if (d && d.success && d.data) {
          setRfq(d.data);
        } else {
          setError(displayApiError(d, t, 'errPromiseNotFound'));
        }
        setLoading(false);
      })
      .catch(function(e){
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork')); setLoading(false);
      });
  }, [rfqId, navigate, t]);

  useEffect(function(){ load(); }, [load]);

  var handleWithdraw = function() {
    if (!rfq || !rfq.ownQuote) return;
    // P2 #8 (2026-05-24) — window.confirm → ConfirmDialog, window.alert → showToast(error)
    confirm({ message: t('promiseDetailWithdrawConfirm'), variant: 'danger' }).then(function(ok){
      if (!ok) return;
      authBuyerFetch(API + '/buyer-app/rfq/' + rfq.id + '/quotes/' + rfq.ownQuote.id, { method: 'DELETE' })
        .then(function(r){
          if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
          return r.json();
        })
        .then(function(d){
          if (d && d.success) {
            load();
          } else {
            showToast(displayApiError(d, t, 'errWithdrawFailed'), { variant: 'error' });
          }
        })
        .catch(function(e){ if (!(e && e.message === 'unauthorized')) showToast(t('commonNetwork'), { variant: 'error' }); });
    });
  };

  if (loading) {
    return <AuctionBuyerLayout activeKey="promise"><div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>{t('auctionsListLoading')}</div></AuctionBuyerLayout>;
  }
  if (error || !rfq) {
    return (
      <AuctionBuyerLayout activeKey="promise">
        <div style={{ textAlign: 'center', padding: 80 }}>
          <div style={{ color: '#dc2626', marginBottom: 16 }}>{error || t('errPromiseNotFound')}</div>
          <button type="button" onClick={function(){navigate('/buyer/promise');}}
            style={{ padding: '10px 20px', background: '#1B3A5C', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', }}>
            {t('detailBackToListShort')}
          </button>
        </div>
      </AuctionBuyerLayout>
    );
  }

  var photoUrls = getPhotoUrls(rfq);
  var hasMyQuote = !!rfq.ownQuote;
  var myQuoteStatus = hasMyQuote ? rfq.ownQuote.status : null;
  var isActive = rfq.status === 'open';
  var canQuote = isActive && (!hasMyQuote || (myQuoteStatus === 'active' && (rfq.ownQuote.updateCount || 0) < 1));
  var statusLabel = myQuoteStatus === 'active' ? t('quoteStatusActive')
    : myQuoteStatus === 'withdrawn' ? t('quoteStatusWithdrawn')
    : myQuoteStatus === 'committed' ? t('quoteStatusCommitted')
    : myQuoteStatus;

  return (
    <AuctionBuyerLayout activeKey="promise">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px 100px' }}>
        <button type="button" onClick={function(){navigate('/buyer/promise');}}
          style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '8px 0', marginBottom: 16, }}>
          {t('promiseDetailBackToList')}
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 24, alignItems: 'start' }}>
          {/* 좌측 — 사진 + 정보 */}
          <div>
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {photoUrls.length > 0 ? (
                <img src={photoUrls[photoIdx]} alt=""
                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#f1f5f9' }}
                />
              ) : (
                <div style={{ width: '100%', aspectRatio: '4/3', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                  {t('cardNoPhoto')}
                </div>
              )}
              {photoUrls.length > 1 && (
                <div style={{ display: 'flex', gap: 4, padding: 8, overflowX: 'auto', borderTop: '1px solid #f1f5f9' }}>
                  {photoUrls.map(function(url, i){
                    return (
                      <button key={i} type="button" onClick={function(){setPhotoIdx(i);}}
                        style={{
                          flex: '0 0 80px', width: 80, height: 60, padding: 0,
                          border: photoIdx === i ? '2px solid #1B3A5C' : '1px solid #e2e8f0',
                          borderRadius: 6, overflow: 'hidden', cursor: 'pointer', background: 'white',
                        }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', marginTop: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                {rfq.year ? rfq.year + ' ' : ''}{rfq.model || t('promiseCardCarFallback')}
              </h2>
              {/* Wave R8.4c-D (2026-05-24) — 빈 값일 때도 라벨 + "—" 표시 → 출품자 미입력
                  가시화. 기존: falsy 면 아예 안 보임 → 사용자가 데이터 부재인지 화면 부재인지 혼동. */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                <span>{t('detailCarNumberLabel')}: <strong style={{ color: rfq.carNo ? '#0f172a' : '#cbd5e1' }}>{rfq.carNo || '—'}</strong></span>
                <span>VIN: <strong style={{ color: rfq.vin ? '#0f172a' : '#cbd5e1' }}>{rfq.vin || '—'}</strong></span>
                <span>{t('detailMileageLabel')}: <strong style={{ color: rfq.mileage != null ? '#0f172a' : '#cbd5e1' }}>{rfq.mileage != null ? Number(rfq.mileage).toLocaleString() + ' km' : '—'}</strong></span>
                <span>{t('detailFuelLabel')}: <strong style={{ color: rfq.fuel ? '#0f172a' : '#cbd5e1' }}>{rfq.fuel || '—'}</strong></span>
                <span>{t('detailColorLabel')}: <strong style={{ color: rfq.color ? '#0f172a' : '#cbd5e1' }}>{rfq.color || '—'}</strong></span>
              </div>
              {/* W6 — AUTODEX 시세 카드 (외부 바이어, KRW+USD) */}
              <MarketPriceCard model={rfq.model} year={rfq.year} mileage={rfq.mileage} locale={loc.locale} showUsd={true} />

              {rfq.memo && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>{t('promiseDetailSellerMemo')}</div>
                  <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{rfq.memo}</div>
                </div>
              )}
              {rfq.ownerConsented && (
                <div style={{ marginTop: 16, padding: 10, background: '#ecfdf5', borderRadius: 8, fontSize: 12, color: '#065f46', fontWeight: 600 }}>
                  {t('promiseDetailOwnerConsented')}
                </div>
              )}
            </div>

            {/* Wave R8.4c — 자가검차 평가지 readonly 요약 (Flutter R8.4b 동등).
                i18n: TW-1.f.7 (2026-05-24) — 4 locale (ko/en/ar/ru) 등록 완료. */}
            {rfq.selfInspection && (
              <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', marginTop: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                  🔍 {t('promiseDetailSelfInspection')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                  {/* 사고 이력 */}
                  <div style={{
                    background: rfq.selfInspection.isAccidentFree === true ? '#dcfce7' : '#f8fafc',
                    border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                      {t('promiseDetailAccidentHistory')}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {rfq.selfInspection.isAccidentFree === true
                        ? <span style={{ color: '#15803d' }}>✓ {t('promiseDetailAccidentFree')}</span>
                        : (Object.keys(rfq.selfInspection.damageData || {}).length > 0
                            ? <span style={{ color: '#b45309' }}>⚠ {t('promiseDetailAccidentParts', { n: Object.keys(rfq.selfInspection.damageData || {}).length })}</span>
                            : <span style={{ color: '#94a3b8' }}>{t('promiseDetailUnchecked')}</span>)}
                    </div>
                  </div>
                  {/* 도막 측정 */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                      {t('promiseDetailPaintMeasure')}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {(function(){
                        var n = Object.keys(rfq.selfInspection.paintData || {}).length;
                        return n > 0
                          ? <span style={{ color: '#1d4ed8' }}>{t('promiseDetailPaintParts', { n: n })}</span>
                          : <span style={{ color: '#94a3b8' }}>{t('promiseDetailNotEntered')}</span>;
                      })()}
                    </div>
                  </div>
                  {/* 종합 점수 */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                      {t('promiseDetailTotalScore')}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {(rfq.selfInspection.score === 0 || rfq.selfInspection.score > 0)
                        ? <span style={{ color: '#1B3A5C', fontWeight: 800 }}>⭐ {rfq.selfInspection.score}/100</span>
                        : <span style={{ color: '#94a3b8' }}>{t('promiseDetailNotEntered')}</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 우측 — 시세 sticky 카드 */}
          <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e2e8f0', position: 'sticky', top: 24 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{t('promiseDetailQuoteCount')}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1B3A5C', marginTop: 4 }}>
              {rfq.quoteCount || 0}
            </div>

            {hasMyQuote && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 14, marginTop: 16 }}>
                <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 600, marginBottom: 4 }}>{t('promiseDetailMyQuote')}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1e40af' }}>
                  {formatKrwAmountWithConvert(rfq.ownQuote.priceKrw, t, loc.locale, rateState.rates)}
                </div>
                <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 4 }}>
                  {t('promiseDetailStatus', { status: statusLabel, n: rfq.ownQuote.updateCount || 0 })}
                </div>
                {rfq.ownQuote.memo && (
                  <div style={{ fontSize: 12, color: '#1e40af', marginTop: 8, paddingTop: 8, borderTop: '1px solid #bfdbfe' }}>{rfq.ownQuote.memo}</div>
                )}
              </div>
            )}

            {!isActive && (
              <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: 12, marginTop: 16, fontSize: 13, color: '#92400e' }}>
                {rfq.status === 'committed' ? t('rfqStatusCommittedNote') : (rfq.status === 'completed' ? t('rfqStatusCompletedNote') : t('rfqStatusClosedNote'))}
              </div>
            )}

            <button type="button" onClick={function(){setQuoteModalOpen(true);}}
              disabled={!canQuote}
              style={{
                width: '100%', height: 52,
                background: canQuote ? 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)' : '#cbd5e1',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700,
                cursor: canQuote ? 'pointer' : 'not-allowed',
                marginTop: 20,
                boxShadow: canQuote ? '0 4px 12px rgba(27,58,92,0.2)' : 'none',
              }}>
              {!isActive ? t('promiseDetailClosedCta') : (hasMyQuote ? (canQuote ? t('quoteModalTitleEdit') : t('detailUpdateLimitReached')) : t('quoteModalTitleNew'))}
            </button>

            {hasMyQuote && myQuoteStatus === 'active' && isActive && (
              <button type="button" onClick={handleWithdraw}
                style={{
                  width: '100%', height: 40, marginTop: 10,
                  background: 'white', color: '#dc2626',
                  border: '1.5px solid #fecaca', borderRadius: 8,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',                }}>
                {t('promiseDetailWithdrawCta')}
              </button>
            )}

            {rfq.sellerTier && rfq.sellerTier.label && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>{t('promiseDetailSellerTier')}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: rfq.sellerTier.color || '#0f172a' }}>{rfq.sellerTier.label}</div>
                {rfq.sellerTier.completionRate != null && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {t('promiseDetailSellerStats', { pct: Math.round(rfq.sellerTier.completionRate * 100), count: rfq.sellerTier.sampleSize || 0 })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {quoteModalOpen && (
          <AuctionBuyerQuoteModal rfq={rfq}
            existingQuote={hasMyQuote && myQuoteStatus === 'active' ? rfq.ownQuote : null}
            onClose={function(){setQuoteModalOpen(false);}}
            onSuccess={function(){ load(); }}
          />
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerPromiseDetail;
